import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialInstitutionItemActions from "../../../actions/trialInstitutionItem";
import { InlineLoading } from "../../../components/common";
import { FormFilterInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { InstitutionTrialsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { findLookupValue } from "../../../helpers/lookupHelper";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { IFormFilter, ITmdState } from "../../../reducers";

interface InstitutionTrialsPageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    trialInstitutions: Dtos.TrialInstitution[];
    loadingTrialInstitutions: boolean;
    loadTrialInstitutionsSuccess: boolean;
    loadTrialInstitutionsFailure: boolean;
    loadTrialInstitutionsErrorCode: string;
    formFilter: IFormFilter;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    lookups: Dtos.Lookup[];
    trialInstitutionItems: Dtos.TrialInstitutionItem[];
    loadingTrialInstitutionItems: boolean;
    loadTrialInstitutionItemsSuccess: boolean;
    loadTrialInstitutionItemsFailure: boolean;
    loadTrialInstitutionItemsErrorCode: string;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearTrialInstitutions: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitutions: TrialInstitutionActions.ITrialInstitutionsLoadByInstitutionIdActionCreator;
    filter: TrialInstitutionActions.ITrialInstitutionFilterActionCreator;
    clearTrialInstitutionItems: TrialInstitutionItemActions.ITrialInstitutionItemClearActionCreator;
    loadTrialInstitutionItems: TrialInstitutionItemActions.ITrialInstitutionItemsLoadSiteClosureActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionTrialsPage: FC<InstitutionTrialsPageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    trialInstitutions,
    loadingTrialInstitutions,
    loadTrialInstitutionsSuccess,
    loadTrialInstitutionsFailure,
    loadTrialInstitutionsErrorCode,
    formFilter,
    permissions,
    pageSize,
    lookups,
    trialInstitutionItems,
    //loadingTrialInstitutionItems,
    loadTrialInstitutionItemsSuccess,
    loadTrialInstitutionItemsFailure,
    loadTrialInstitutionItemsErrorCode,
    clearInstitution,
    loadInstitution,
    clearTrialInstitutions,
    loadTrialInstitutions,
    filter,
    clearTrialInstitutionItems,
    loadTrialInstitutionItems,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitutionTrials = 
        !loadingInstitution &&
        loadInstitutionSuccess &&
        !loadingTrialInstitutions &&
        loadTrialInstitutionsSuccess;


    // Load Institution & Trial institutions
    useEffect(() => {
        if (institutionIdNumber && !institution && !loadingInstitution) {
            setDrawerItems(InstitutionSubMenu(institutionIdNumber, permissions));
            setBreadcrumbs(InstitutionTrialsBreadcrumbs(institutionIdNumber));

            loadInstitution(institutionIdNumber);
            loadTrialInstitutions(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        institution,
        loadingInstitution,
        permissions
    ]);

    // Load Trial Institution Items
    useEffect(() => {
        if (trialInstitutions && trialInstitutions.length > 0 && !initalised) {
            const trialInstitutionIds = trialInstitutions.map(x => x.id);
            loadTrialInstitutionItems(trialInstitutionIds);
            setInitalised(true);
        }
    }, [trialInstitutions, initalised]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution) {
            setBreadcrumbs(
                InstitutionTrialsBreadcrumbs(
                    institutionIdNumber,
                    institution?.name
                )
            );
        }
    }, [institutionIdNumber, institution]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadTrialInstitutionsFailure) {
            navigate(`/error/${loadTrialInstitutionsErrorCode}`);
        }
        if (loadTrialInstitutionItemsFailure) {
            navigate(`/error/${loadTrialInstitutionItemsErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadInstitutionErrorCode,
        loadTrialInstitutionsFailure,
        loadTrialInstitutionsErrorCode,
        loadTrialInstitutionItemsFailure,
        loadTrialInstitutionItemsErrorCode
    ]);

    // Cleanup effect to clear the institution & trials on component unmount
    useEffect(() => {
        return () => {
            clearInstitution();
            clearTrialInstitutions();
            clearTrialInstitutionItems();
        };
    }, [clearInstitution, clearTrialInstitutions, clearTrialInstitutionItems]);

    const createTable = useMemo(() => {
        if (!trialInstitutions || trialInstitutions.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>There are currently no trials to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = trialInstitutions.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialInstitutions}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : trialInstitutions.length}
                    pageSize={showPagination ? pageSize : trialInstitutions.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trials to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`/trials/${rowInfo.original.trialId}/trialInstitution/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "trial.displayName",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.TRIAL} fixedWidth />
                            ),
                        },
                        {
                            id: "trial.displayName",
                            Header: "Trial",
                            accessor: "trial.displayName",
                        },
                        {
                            id: "trialInstitutionStatusId",
                            Header: "Status",
                            accessor: "trialInstitutionStatusId",
                            Cell: (props) => {
                                return (
                                    <span>
                                        {findLookupValue(
                                            "TrialInstitution",
                                            "TrialInstitutionStatusId",
                                            props.value ?? props?.original?.trialInstitutionStatusId,
                                            lookups
                                        )}
                                    </span>
                                );
                            },

                        },
                        {
                            id: "dateActivated",
                            Header: "Date Activated",
                            accessor: "dateActivated",
                            Cell: (props) => {
                                const dateValue = props.value ?? props?.original?.dateActivated;

                                return (
                                    <span>
                                        {
                                            dateValue
                                                ? moment(dateValue, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : ""
                                        }   
                                    </span>
                                );
                            },
                        },
                        {
                            id: "closureDate",
                            Header: "Closed",
                            accessor: "closureDate",
                            Cell: (props) => {
                                const dateValue = trialInstitutionItems.find(x => x.trialInstitutionId == props.original.id)
                                                    ?.collectionInstance?.collectionInstanceDatas?.find(x => x.itemComponentId == 404 && x.dateValue != null)
                                                    ?.dateValue;

                                return (
                                    <span>
                                        {
                                            dateValue
                                                ? moment(dateValue, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : !loadTrialInstitutionItemsSuccess 
                                                    ? <InlineLoading size={20} /> 
                                                    : ""
                                        }   
                                    </span>
                                );
                            },
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialInstitutions,
        pageSize,
        trialInstitutionItems,
        loadTrialInstitutionItemsSuccess
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trials</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                            hideActiveToggle={true}
                        />
                    </div>
                </div>
                {!showInstitutionTrials ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionTrials ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        permissions,
        showInstitutionTrials,
        createTable,
        institutionIdNumber
    ]);


    return (
        <RestrictedLayout
            loading={loadingInstitution || loadingTrialInstitutions}
            permission={Dtos.Permission.TrialInstitutionView}
        >
            { renderContent }
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<InstitutionTrialsPageProps> => {
    let institution: Dtos.Institution | undefined = 
        !(state.institutions.formData instanceof Array)
            ? state.institutions.formData
            : undefined;
    let trialInstitutions: Dtos.TrialInstitution[] | undefined = 
        (state.trialInstitution.formFilteredData instanceof Array)
            ? state.trialInstitution.formFilteredData
            : undefined;
    let trialInstitutionItems: Dtos.TrialInstitutionItem[] | undefined = 
        (state.trialInstitutionItem.formFilteredData instanceof Array)
            ? state.trialInstitutionItem.formFilteredData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,

        trialInstitutions: trialInstitutions!,
        loadingTrialInstitutions:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionsSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionsFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionsErrorCode: state.trialInstitution.loadState.errorCode,

        lookups: state.trialInstitution.lookups,
        formFilter: state.trialInstitution.formFilter,

        trialInstitutionItems: trialInstitutionItems!,
        loadingTrialInstitutionItems:
            state.trialInstitutionItem.loadState &&
            state.trialInstitutionItem.loadState.status == RequestState.Pending,
        loadTrialInstitutionItemsSuccess:
            state.trialInstitutionItem.loadState &&
            state.trialInstitutionItem.loadState.status == RequestState.Success,
        loadTrialInstitutionItemsFailure:
            state.trialInstitutionItem.loadState &&
            state.trialInstitutionItem.loadState.status == RequestState.Failure,
        loadTrialInstitutionItemsErrorCode: state.trialInstitutionItem.loadState.errorCode,
        
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionTrialsPageProps> => {
    return {
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch),
        loadTrialInstitutions: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionsByInstitutionId, dispatch),
        clearTrialInstitutions: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        filter: bindActionCreators(TrialInstitutionActions.Filter, dispatch),
        loadTrialInstitutionItems: bindActionCreators(TrialInstitutionItemActions.LoadTrialInstitutionItemsSiteClosure, dispatch),
        clearTrialInstitutionItems: bindActionCreators(TrialInstitutionItemActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionTrialsPage);